<script setup lang="ts" generic="T extends FilterParamsBase">
import { BaseButtonKindEnum } from "@/components/Base/Button";
import SearchInput from "@/components/SearchInput.vue";
import mobileIcon from "@/images/icons/Filter_default.svg";
import { FilterCategory, FilterParamsBase } from "@/types/filter.types";

export type Props<T> = {
  categories: FilterCategory[];
  query: string;
  queryPlaceholder: string;
  currentParams: T;
  hasQuery: boolean;
  defaultParams?: string[];
};

const props = defineProps<Props<T>>();

const emit = defineEmits([
  "update:modelValue",
  "query",
  "reset",
  "finished",
  "submit",
  "toggleMobileFilter",
]);

const internalQuery = ref(props.query);

const showMobileFilter = ref(false);

const toggleMobileFilter = () => {
  showMobileFilter.value = !showMobileFilter.value;
};

const handleFilterChange = (value: {
  label: string;
  value: string | string[];
}) => {
  emit("update:modelValue", value);
};

const handleFinished = () => {
  emit("finished");
  toggleMobileFilter();
};

const handleReset = () => {
  internalQuery.value = "";
  emit("reset");
};

const handleQuery = () => {
  emit("query", internalQuery.value);
};

const handleSubmit = () => {
  emit("submit", internalQuery.value);
};

const handleCancel = () => {
  toggleMobileFilter();
};

const selectedCount = computed(() => {
  let count = 0;
  for (const param in props.currentParams) {
    if (
      props.currentParams[param] !== undefined &&
      !props.defaultParams?.includes(param)
    ) {
      count =
        props.currentParams[param] &&
        typeof props.currentParams[param] === "string"
          ? count + 1
          : count + (props.currentParams[param] as string[]).length;
    }
  }
  return count;
});
</script>

<template>
  <div>
    <form
      class="d-flex align-items-center flex-wrap search-form"
      @submit.prevent="handleSubmit"
    >
      <div class="search-height d-flex w-100 max-width-md mb-3">
        <div class="flex-fill search-form__search-container">
          <SearchInput
            v-model="internalQuery"
            size="md"
            :placeholder="queryPlaceholder"
            @input="handleQuery"
          />
        </div>
        <BaseButton
          class="border-0 bg-transparent d-sm-block d-md-none position-relative py-0"
          :kind="BaseButtonKindEnum.NAKED"
          type="button"
          no-padding
          @click="toggleMobileFilter"
        >
          <img :src="mobileIcon" alt="Toggle mobile filter menu" />
          <BaseNotificationBadge
            v-if="selectedCount > 0"
            :count="selectedCount"
            :show-count="false"
            class="position-absolute"
            style="top: 7px; right: 11px; pointer-events: none"
            size="sm"
          />
        </BaseButton>
      </div>
      <FilterDesktopItems
        :categories="categories"
        :query="query"
        :has-query="hasQuery"
        :current-params="currentParams as T"
        @update:model-value="handleFilterChange"
        @reset="handleReset"
        @submit="handleSubmit"
        @toggle-mobile-filter="toggleMobileFilter"
      />
      <FilterOffCanvas
        :open="showMobileFilter"
        :categories="categories"
        :current-params="currentParams as T"
        :has-query="hasQuery"
        :default-params="defaultParams"
        @cancel="handleCancel"
        @finished="handleFinished"
        @reset="handleReset"
        @update:model-value="handleFilterChange"
      />
    </form>
  </div>
</template>
<style lang="scss" scoped>
.search-height {
  height: 2.375rem;
}

.search-form {
  width: 100%;

  &__search-container {
    width: 100%;
    margin-bottom: $spacer-3;
  }
}
</style>
